import apiClient, { 
  tokenManager, 
  logout as performLogout, 
  performTokenRefresh,
  handleRefreshError,
  forceLogout
} from '../lib/api/client';
import { queryClient } from '../lib/api/queryClient';
import { getApiUrl } from '../config/config';
import { extractErrorMessage } from '../utils/errorHandler';
import { authQueryKeys, verifyTokenQueryFn } from '../lib/state/hooks/useAuthQueries';
import { showSuccess } from '../utils/toast';
import { TOAST_DURATIONS } from '../constants/toastConstants';

const createErrorResponse = (error, defaultMessage = 'An unexpected error occurred') => {
  // Log the error details for debugging
  if (error.response?.data) {
    console.log('🔍 [Auth] Server error response:', JSON.stringify(error.response.data, null, 2));
  }
  
  // Extract error message using centralized extraction function
  const errorMessage = extractErrorMessage(
    error.response?.data || error,
    defaultMessage
  );
  
  // Log if extracted message is the default (indicates extraction may have failed)
  if (errorMessage === defaultMessage) {
    console.warn('⚠️ [Auth] Using default error message. Server response:', error.response?.data);
  } else {
    console.log('✅ [Auth] Extracted error message:', errorMessage);
  }

  return {
    isSuccess: false,
    error: {
      message: errorMessage,
      code: error.response?.status || 500,
      originalError: error
    }
  };
};

let refreshPromise = null;

const savedToken = localStorage.getItem('access_token');
if (savedToken) {
  tokenManager.setAuthToken(savedToken);
}

const authService = {
  setAuthToken(token) {
    tokenManager.setAuthToken(token);
  },

  setRefreshToken(refreshToken) {
    tokenManager.setRefreshToken(refreshToken);
  },

  setTokens(accessToken, refreshToken) {
    tokenManager.setTokens(accessToken, refreshToken);
  },

  updateAxiosHeaders() {
    tokenManager.updateAxiosHeaders();
  },

  createAuthenticatedAxios() {
    return apiClient;
  },

  clearAuthToken() {
    tokenManager.clearAuthToken();
  },

  getCurrentToken() {
    return tokenManager.getCurrentToken();
  },

  getCurrentRefreshToken() {
    return tokenManager.getCurrentRefreshToken();
  },

  isTokenExpired(token) {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  },

  /**
   * Returns milliseconds until token expiry, or 0 if expired/invalid.
   * Used for scheduling auto logout.
   */
  getTokenExpiryMs(token) {
    if (!token) return 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload.exp !== 'number') return 0;
      const expiryMs = payload.exp * 1000 - Date.now();
      return expiryMs > 0 ? expiryMs : 0;
    } catch {
      return 0;
    }
  },


  async verifyToken(token = null) {
    const tokenToVerify = token || this.getCurrentToken();
    
    if (!tokenToVerify) {
      return { isValid: false, error: 'No token provided' };
    }

    try {
      const result = await queryClient.fetchQuery({
        queryKey: authQueryKeys.verifyToken(tokenToVerify),
        queryFn: () => verifyTokenQueryFn(tokenToVerify),
        staleTime: 5 * 1000, // 5 seconds - very short cache
        gcTime: 10 * 1000, // 10 seconds
      });

      return result;
    } catch (error) {
      return { 
        isValid: false, 
        error: extractErrorMessage(
          error.response?.data || error,
          'Token verification failed'
        )
      };
    }
  },

  async refreshAccessToken() {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        const refreshToken = this.getCurrentRefreshToken();
        if (!refreshToken) {
          forceLogout('No refresh token available').catch(() => {});
          return { success: false, error: 'No refresh token available', shouldLogout: true };
        }

        const { access: newAccessToken, refresh: newRefreshToken } = await performTokenRefresh(refreshToken);
        this.setTokens(newAccessToken, newRefreshToken);
        
        return { 
          success: true, 
          accessToken: newAccessToken,
          refreshToken: newRefreshToken
        };
      } catch (error) {
        const errorInfo = handleRefreshError(error);
        return {
          success: false,
          error: errorInfo.message,
          shouldLogout: errorInfo.shouldLogout,
          errorType: errorInfo.type,
          retryable: errorInfo.retryable,
          statusCode: errorInfo.statusCode
        };
      } finally {
        setTimeout(() => { refreshPromise = null; }, 0);
      }
    })();

    return refreshPromise;
  },

  async login(username, password) {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'LOGIN'), {
        username,
        password
      }, {
        // Skip token refresh interceptor for login endpoint
        // Login is for unauthenticated users and should not trigger session expired
        skipAuthRefresh: true
      });
      
      if (response.data?.isSuccess && response.data?.data) {
        const { user, tokens } = response.data.data;
        const accessToken = tokens?.access;
        
        if (accessToken) {
          this.setTokens(accessToken, tokens?.refresh);
          
          return {
            ...response.data,
            data: {
              ...response.data.data,
              user: {
                ...user,
                fullname: user.username,
                email: user.email,
                role: user.role,
                active_tenant_id: user.active_tenant_id,
                tenant_info: user.tenant_info
              }
            }
          };
        }
        
        return {
          isSuccess: false,
          error: { message: 'No access token received from server' }
        };
      }
      
      return response.data;
    } catch (error) {
      // Don't use a specific default message - let the server error show through
      return createErrorResponse(error, 'Login failed. Please try again.');
    }
  },

  async signup(userData) {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'SIGNUP'), userData, {
        // Skip token refresh interceptor for signup endpoint
        skipAuthRefresh: true
      });
      return response.data;
    } catch (error) {
      return createErrorResponse(error);
    }
  },

  async forgotPassword(username) {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'FORGOT_PASSWORD'), { username }, {
        // Skip token refresh interceptor for forgot password endpoint
        skipAuthRefresh: true
      });
      return response.data;
    } catch (error) {
      return createErrorResponse(error);
    }
  },

  async resetPassword(token, newPassword) {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'RESET_PASSWORD'), {
        token,
        new_password: newPassword
      }, {
        // Skip token refresh interceptor for reset password endpoint
        skipAuthRefresh: true
      });
      return response.data;
    } catch (error) {
      return createErrorResponse(error);
    }
  },

  async changePassword(current_password, new_password) {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'CHANGE_PASSWORD'), {
        current_password, new_password, new_password
      });
      return response.data;
    } catch (error) {
      return createErrorResponse(error);
    }
  },

  async approveUser(token) {
    try {
      const response = await apiClient.get(getApiUrl('AUTH', 'APPROVE_USER'), {
        params: { token },
        // Skip token refresh interceptor for approve user endpoint
        skipAuthRefresh: true
      });
      return response.data;
    } catch (error) {
      return createErrorResponse(error);
    }
  },

  async getTenantConfig() {
    try {
      const response = await apiClient.get(getApiUrl('AUTH', 'TENANT_CONFIG'));
      
      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null
        };
      }
      
      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to fetch tenant configuration' }
      };
    } catch (error) {
      // Use centralized error extraction
      const errorMessage = extractErrorMessage(
        error.response?.data || error,
        'Failed to fetch tenant configuration'
      );
      
      return {
        isSuccess: false,
        data: null,
        error: {
          message: errorMessage,
          code: error.response?.status || 500,
          originalError: error
        }
      };
    }
  },

  async getAiAgentEnum() {
    try {
      const response = await apiClient.get(getApiUrl('AUTH', 'AI_AGENT_ENUM'));
      
      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data || [],
          error: null
        };
      }
      
      return {
        isSuccess: false,
        data: [],
        error: response.data?.error || { message: 'Failed to fetch AI agent enum' }
      };
    } catch (error) {
      // Use centralized error extraction
      const errorMessage = extractErrorMessage(
        error.response?.data || error,
        'Failed to fetch AI agent enum'
      );
      
      return {
        isSuccess: false,
        data: [],
        error: {
          message: errorMessage,
          code: error.response?.status || 500,
          originalError: error
        }
      };
    }
  },

  async logout(reason = 'Session expired') {
    try {
      // Get the current access token before clearing it
      const token = this.getCurrentToken();
      
      // Only call API if we have a token
      if (token) {
        // Call the logout API endpoint with Authorization header
        const response = await apiClient.post(getApiUrl('AUTH', 'LOGOUT'), {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          // Skip token refresh interceptor for logout endpoint
          skipAuthRefresh: true,
          // Use shorter timeout for logout to avoid blocking the UI
          timeout: 5000
        });
        
        console.log('✅ Logout API called successfully');
        
        // Show success toast message from server response
        // if (response.data?.isSuccess && response.data?.data?.message) {
        //   showSuccess(response.data.data.message, {
        //     duration: TOAST_DURATIONS.MEDIUM
        //   });
        // } else {
        //   // Fallback message if server doesn't provide one
        //   showSuccess('Logged out successfully', {
        //     duration: TOAST_DURATIONS.MEDIUM
        //   });
        // }
      }
    } catch (error) {
      // Don't block logout flow if API call fails
      console.warn('⚠️ Logout API call failed (continuing with logout):', error.message);
    } finally {
      // Always perform local logout regardless of API response
      await performLogout(reason);
    }
  },

  async updateTenantConfig(configData) {
    try {
      const response = await apiClient.patch(getApiUrl('AUTH', 'TENANT_CONFIG'), configData);
      
      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null
        };
      }
      
      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to update tenant configuration' }
      };
    } catch (error) {
      // Use centralized error extraction
      const errorMessage = extractErrorMessage(
        error.response?.data || error,
        'Failed to update tenant configuration'
      );
      
      return {
        isSuccess: false,
        data: null,
        error: {
          message: errorMessage,
          code: error.response?.status || 500,
          originalError: error
        }
      };
    }
  }
};

export { authService };
